import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { IBusinessCategory } from '../../../_interfaces/IBusinessCategory';
import { BusinessCategoriesService } from '../../../_services/business-categories/business-categories.service';

type EditableCategory = IBusinessCategory & {
  _editing?: boolean;
  _draftName?: string;
  _draftActive?: boolean;
};

@Component({
  selector: 'app-admin-business-categories',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    TableModule,
    InputTextModule,
    ButtonModule,
  ],
  templateUrl: './admin-business-categories.component.html',
  styleUrl: './admin-business-categories.component.scss',
})
export class AdminBusinessCategoriesComponent implements OnInit {
  loading = true;
  saving = false;
  rows: EditableCategory[] = [];
  newCategoryName = '';

  constructor(
    private readonly categoriesService: BusinessCategoriesService,
    private readonly messageService: MessageService,
    private readonly translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.load();
  }

  load() {
    this.loading = true;
    this.categoriesService.getAll().subscribe({
      next: (rows) => {
        this.rows = (rows || []).map((row) => ({
          ...row,
          _editing: false,
          _draftName: row.name,
          _draftActive: !!row.active,
        }));
        this.loading = false;
      },
      error: () => {
        this.rows = [];
        this.loading = false;
      },
    });
  }

  create() {
    const name = `${this.newCategoryName || ''}`.trim();
    if (!name || this.saving) return;

    this.saving = true;
    this.categoriesService
      .create({
        name,
        active: true,
      })
      .subscribe({
        next: () => {
          this.newCategoryName = '';
          this.toastSuccess('ADMIN.BUSINESS_CATEGORIES.MSG.CREATE_OK');
          this.saving = false;
          this.load();
        },
        error: () => {
          this.toastError('ADMIN.BUSINESS_CATEGORIES.MSG.CREATE_ERROR');
          this.saving = false;
        },
      });
  }

  startEdit(row: EditableCategory) {
    row._editing = true;
    row._draftName = row.name;
    row._draftActive = !!row.active;
  }

  cancelEdit(row: EditableCategory) {
    row._editing = false;
    row._draftName = row.name;
    row._draftActive = !!row.active;
  }

  saveRow(row: EditableCategory) {
    if (!row.id || this.saving) return;
    const name = `${row._draftName || ''}`.trim();
    if (!name) return;

    this.saving = true;
    this.categoriesService
      .update(row.id, {
        name,
        active: !!row._draftActive,
      })
      .subscribe({
        next: () => {
          this.toastSuccess('ADMIN.BUSINESS_CATEGORIES.MSG.UPDATE_OK');
          this.saving = false;
          this.load();
        },
        error: () => {
          this.toastError('ADMIN.BUSINESS_CATEGORIES.MSG.UPDATE_ERROR');
          this.saving = false;
        },
      });
  }

  disable(row: EditableCategory) {
    if (!row.id || this.saving) return;
    this.saving = true;
    this.categoriesService.disable(row.id).subscribe({
      next: () => {
        this.toastSuccess('ADMIN.BUSINESS_CATEGORIES.MSG.DISABLE_OK');
        this.saving = false;
        this.load();
      },
      error: () => {
        this.toastError('ADMIN.BUSINESS_CATEGORIES.MSG.DISABLE_ERROR');
        this.saving = false;
      },
    });
  }

  private toastSuccess(detailKey: string) {
    this.messageService.add({
      severity: 'success',
      summary: this.translate.instant('COMMON.SUCCESS'),
      detail: this.translate.instant(detailKey),
    });
  }

  private toastError(detailKey: string) {
    this.messageService.add({
      severity: 'error',
      summary: this.translate.instant('COMMON.ERROR'),
      detail: this.translate.instant(detailKey),
    });
  }
}
